import type { GeoPoint } from '@lib/dto/geo.dto'
import { isPointInRing } from '@lib/helpers/geo'

/*
 * Which color each region on a map is drawn in: areas on the boundary page
 * and the proposal plan, zones on the irrigation survey (docs/map-regions.md).
 *
 * Every region drawn in one color told a crew nothing about where one
 * stopped and the next began, and a site has more regions than any set of
 * colors can keep apart (an office park with 29 areas). So the rule is the
 * printed map's: neighbors never share a color, regions far apart may, and
 * the name or number on each is what identifies it.
 */

/** The hues regions are drawn in, by the names their tokens carry. */
export const REGION_COLORS = [
  'yellow',
  'cyan',
  'pink',
  'lime',
  'orange',
  'violet',
  'blue',
  'coral',
] as const

export type RegionColor = (typeof REGION_COLORS)[number]

export interface Region {
  /**
   * Rings with the same key are one region drawn in pieces, and share a
   * color: on a proposal, two rings called "Common Area" are one choice.
   */
  key: string
  ring: readonly GeoPoint[]
}

/**
 * How close, in meters, two rings come before they count as neighbors.
 * Rings drawn by hand to meet along a fence leave slivers between them, and
 * two colors a meter apart are side by side to anyone looking.
 */
const NEIGHBOR_M = 3

const METERS_PER_DEGREE_LATITUDE = 110_574

interface Plane {
  x: number
  y: number
}

/**
 * Points on a plane in meters, scaled at one latitude. Over a site this is
 * as good as the sphere for "how far apart", which is all it is used for.
 */
function toPlane(latitude: number): (point: GeoPoint) => Plane {
  const xScale =
    METERS_PER_DEGREE_LATITUDE * Math.cos((latitude * Math.PI) / 180)
  return (point) => ({
    x: point.longitude * xScale,
    y: point.latitude * METERS_PER_DEGREE_LATITUDE,
  })
}

function distanceToSegment(point: Plane, a: Plane, b: Plane): number {
  const dx = b.x - a.x
  const dy = b.y - a.y
  const length = dx * dx + dy * dy
  const t =
    length === 0
      ? 0
      : Math.max(
          0,
          Math.min(1, ((point.x - a.x) * dx + (point.y - a.y) * dy) / length),
        )
  return Math.hypot(point.x - (a.x + t * dx), point.y - (a.y + t * dy))
}

/** The edges of a closed ring, each as its two ends. */
function edgesOf(ring: readonly Plane[]): [Plane, Plane][] {
  return ring.flatMap((point, index): [Plane, Plane][] => {
    const next = ring[(index + 1) % ring.length]
    return next === undefined ? [] : [[point, next]]
  })
}

function crosses(a: [Plane, Plane], b: [Plane, Plane]): boolean {
  const side = (p: Plane, q: Plane, r: Plane) =>
    Math.sign((q.x - p.x) * (r.y - p.y) - (q.y - p.y) * (r.x - p.x))
  return (
    side(a[0], a[1], b[0]) !== side(a[0], a[1], b[1]) &&
    side(b[0], b[1], a[0]) !== side(b[0], b[1], a[1])
  )
}

interface Shape {
  key: string
  ring: readonly GeoPoint[]
  plane: Plane[]
  box: { minX: number; minY: number; maxX: number; maxY: number }
}

/** Whether two rings come within `NEIGHBOR_M` of each other, or overlap. */
function areNeighbors(left: Shape, right: Shape): boolean {
  if (
    left.box.minX > right.box.maxX + NEIGHBOR_M ||
    right.box.minX > left.box.maxX + NEIGHBOR_M ||
    left.box.minY > right.box.maxY + NEIGHBOR_M ||
    right.box.minY > left.box.maxY + NEIGHBOR_M
  ) {
    return false
  }
  const first = left.ring[0]
  const other = right.ring[0]
  if (first !== undefined && isPointInRing(first, [...right.ring])) return true
  if (other !== undefined && isPointInRing(other, [...left.ring])) return true
  const leftEdges = edgesOf(left.plane)
  const rightEdges = edgesOf(right.plane)
  for (const edge of leftEdges) {
    for (const against of rightEdges) {
      if (crosses(edge, against)) return true
    }
  }
  const near = (points: Plane[], edges: [Plane, Plane][]) =>
    points.some((point) =>
      edges.some(([a, b]) => distanceToSegment(point, a, b) <= NEIGHBOR_M),
    )
  return near(left.plane, rightEdges) || near(right.plane, leftEdges)
}

/**
 * A color for each region key: never one a neighbor already has, and among
 * the rest the one used least so far, so a site with eight regions or fewer
 * gives every one its own. Keys are colored in the order first given, so a
 * region added later leaves the ones before it as they were.
 *
 * A region crowded by more neighbors than there are colors (zones may
 * overlap without limit) takes the color fewest of them have.
 */
export function regionColors(
  regions: readonly Region[],
): Map<string, RegionColor> {
  const first = regions[0]?.ring[0]
  if (first === undefined) return new Map()
  const project = toPlane(first.latitude)
  const shapes: Shape[] = regions
    .filter((region) => region.ring.length >= 3)
    .map((region) => {
      const plane = region.ring.map(project)
      const xs = plane.map((point) => point.x)
      const ys = plane.map((point) => point.y)
      return {
        key: region.key,
        ring: region.ring,
        plane,
        box: {
          minX: Math.min(...xs),
          minY: Math.min(...ys),
          maxX: Math.max(...xs),
          maxY: Math.max(...ys),
        },
      }
    })

  const keys = [...new Set(shapes.map((shape) => shape.key))]
  const neighbors = new Map(keys.map((key) => [key, new Set<string>()]))
  for (const [index, left] of shapes.entries()) {
    for (const right of shapes.slice(index + 1)) {
      if (left.key !== right.key && areNeighbors(left, right)) {
        neighbors.get(left.key)?.add(right.key)
        neighbors.get(right.key)?.add(left.key)
      }
    }
  }

  const colors = new Map<string, RegionColor>()
  const uses = new Map<RegionColor, number>(
    REGION_COLORS.map((color) => [color, 0]),
  )
  for (const key of keys) {
    const beside = new Map<RegionColor, number>()
    for (const neighbor of neighbors.get(key) ?? []) {
      const color = colors.get(neighbor)
      if (color !== undefined) beside.set(color, (beside.get(color) ?? 0) + 1)
    }
    const fewest = Math.min(
      ...REGION_COLORS.map((color) => beside.get(color) ?? 0),
    )
    const color = REGION_COLORS.filter(
      (candidate) => (beside.get(candidate) ?? 0) === fewest,
    ).reduce((best, candidate) =>
      (uses.get(candidate) ?? 0) < (uses.get(best) ?? 0) ? candidate : best,
    )
    colors.set(key, color)
    uses.set(color, (uses.get(color) ?? 0) + 1)
  }
  return colors
}
